import { useEffect, useState, type ReactNode } from 'react';
import {
  Button,
  Checkbox,
  Flash,
  FormControl,
  Select,
  Textarea,
  TextInput,
} from '@primer/react';
import { CopyIcon, KeyIcon, PencilIcon, PlusIcon, SyncIcon } from '@primer/octicons-react';

import type { WebhookEndpointView, WebhookSecretView } from '../../client/types.js';
import {
  batchTone,
  blankWebhookForm,
  visibleWebhooksForTeam,
  validateWebhookForm,
  type WebhookForm,
} from '../../logic/webhooks.js';
import { useConsoleMutation, useRoles, useWebhookBatches, useWebhooks } from '../queries.js';
import { Chip, QueryPhase, Section } from '../components/state.js';
import { useStoreState } from '../store-context.js';

function endpointUrl(slug: string): string {
  return new URL(`/v1/hooks/${encodeURIComponent(slug)}`, globalThis.location?.origin ?? 'http://localhost').toString();
}

export function WebhooksScreen(): ReactNode {
  const endpoints = useWebhooks();
  const roles = useRoles();
  const teamId = useStoreState((state) => state.currentTeamId);
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<WebhookSecretView | null>(null);

  const create = useConsoleMutation<WebhookForm, WebhookSecretView>(
    (data, context, input) => data.createWebhook(input, context),
    [['webhooks']],
  );

  if (endpoints.isPending || endpoints.error !== null) {
    return <QueryPhase isPending={endpoints.isPending} error={endpoints.error} source="webhooks" rows={4} />;
  }

  const roleIds = (roles.data ?? []).map((role) => role.roleId);
  const visibleEndpoints = visibleWebhooksForTeam(teamId, endpoints.data ?? []);
  return (
    <div className="stack">
      {revealed === null ? null : (
        <OneTimeSecret value={revealed} onDone={() => setRevealed(null)} />
      )}

      <Section
        label="Inbound webhooks"
        lede="External systems can wake one agent. Requests are rate-limited and collected into one fixed batch before a model is started."
      >
        <Button leadingVisual={PlusIcon} onClick={() => setCreating((value) => !value)}>
          {creating ? 'Cancel' : 'New webhook'}
        </Button>
        {creating ? (
          <WebhookEditor
            initial={blankWebhookForm(roleIds[0] ?? '')}
            roles={roleIds}
            submitLabel="Create webhook"
            busy={create.isPending}
            onSubmit={(form) =>
              create.mutate(form, {
                onSuccess: (created) => {
                  setRevealed(created);
                  setCreating(false);
                },
              })
            }
          />
        ) : null}
      </Section>

      <Section label="Configured endpoints" lede="Pause an endpoint without deleting its history. Rotate its secret whenever a sender changes credentials.">
        {visibleEndpoints.length === 0 ? (
          <p className="placeholder-inline">No webhooks are configured.</p>
        ) : (
          <div className="editor-list">
            {visibleEndpoints.map((endpoint) => (
              <WebhookCard
                key={endpoint.id}
                endpoint={endpoint}
                roles={roleIds}
                onSecret={setRevealed}
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function OneTimeSecret({ value, onDone }: { readonly value: WebhookSecretView; readonly onDone: () => void }): ReactNode {
  const url = endpointUrl(value.endpoint.slug);
  const copy = (text: string): void => { void globalThis.navigator?.clipboard?.writeText(text); };
  return (
    <Flash variant="warning" className="webhook-secret" role="status">
      <strong>Copy this secret now. It will not be shown again.</strong>
      <div className="webhook-copy-row"><code>{url}</code><Button size="small" leadingVisual={CopyIcon} onClick={() => copy(url)}>Copy URL</Button></div>
      {value.secret.length === 0 ? (
        <p>This endpoint has no authentication secret.</p>
      ) : (
        <div className="webhook-copy-row"><code>{value.secret}</code><Button size="small" leadingVisual={KeyIcon} onClick={() => copy(value.secret)}>Copy secret</Button></div>
      )}
      <Button size="small" onClick={onDone}>I saved it</Button>
    </Flash>
  );
}

function WebhookCard({
  endpoint,
  roles,
  onSecret,
}: {
  readonly endpoint: WebhookEndpointView;
  readonly roles: readonly string[];
  readonly onSecret: (value: WebhookSecretView) => void;
}): ReactNode {
  const [editing, setEditing] = useState(false);
  const [rotateMode, setRotateMode] = useState<WebhookForm['authMode']>(endpoint.authMode);
  const [providedSecret, setProvidedSecret] = useState('');
  const batches = useWebhookBatches(endpoint.id, endpoint.teamId);
  const update = useConsoleMutation<Partial<WebhookForm>, WebhookEndpointView>(
    (data, context, input) => data.updateWebhook(endpoint.id, input, context),
    [['webhooks']],
  );
  const rotate = useConsoleMutation<{ readonly authMode: WebhookForm['authMode']; readonly secret?: string }, WebhookSecretView>(
    (data, context, input) => data.rotateWebhookSecret(endpoint.id, input, context),
    [['webhooks']],
  );
  const retry = useConsoleMutation<string, void>(
    (data, context, batchId) => data.retryWebhookBatch(endpoint.id, batchId, context),
    [['webhook-batches'], ['webhooks']],
  );
  const url = endpointUrl(endpoint.slug);
  const initial: WebhookForm = {
    name: endpoint.name,
    targetRoleId: endpoint.targetRoleId,
    authMode: endpoint.authMode,
    enabled: endpoint.enabled,
    signatureHeader: endpoint.signatureHeader,
    deliveryIdHeader: endpoint.deliveryIdHeader,
    rateLimitCount: endpoint.rateLimitCount,
    rateLimitWindowSeconds: endpoint.rateLimitWindowSeconds,
    debounceSeconds: endpoint.debounceSeconds,
    maxPayloadBytes: endpoint.maxPayloadBytes,
    maxBatchEvents: endpoint.maxBatchEvents,
    messageTemplate: endpoint.messageTemplate,
  };

  return (
    <article className="editor-card webhook-card">
      <header className="editor-card-head">
        <div><strong className="editor-title-static">{endpoint.name}</strong><p className="editor-hint">Agent: {endpoint.targetRoleId}</p></div>
        <Chip tone={endpoint.enabled ? 'active' : 'neutral'} text={endpoint.enabled ? 'active' : 'paused'} />
      </header>
      <div className="webhook-copy-row"><code>{url}</code><Button size="small" leadingVisual={CopyIcon} onClick={() => void globalThis.navigator?.clipboard?.writeText(url)}>Copy</Button></div>
      <dl className="webhook-stats">
        <div><dt>Auth</dt><dd>{endpoint.authMode}</dd></div>
        <div><dt>Debounce</dt><dd>{endpoint.debounceSeconds}s</dd></div>
        <div><dt>Accepted</dt><dd>{endpoint.acceptedCount}</dd></div>
        <div><dt>Wake-ups</dt><dd>{endpoint.wakeCount}</dd></div>
        <div><dt>Rate-limited</dt><dd>{endpoint.rateLimitedCount}</dd></div>
        <div><dt>Failures</dt><dd>{endpoint.failureCount}</dd></div>
      </dl>
      <div className="webhook-actions">
        <Button size="small" leadingVisual={PencilIcon} onClick={() => setEditing((value) => !value)}>Edit</Button>
        <Button size="small" onClick={() => update.mutate({ enabled: !endpoint.enabled })}>{endpoint.enabled ? 'Pause' : 'Resume'}</Button>
        <Select aria-label="Authentication after rotation" value={rotateMode} onChange={(event) => setRotateMode(event.target.value as WebhookForm['authMode'])}><Select.Option value="hmac-sha256">HMAC</Select.Option><Select.Option value="bearer">Bearer</Select.Option><Select.Option value="none">None</Select.Option></Select>
        {rotateMode === 'none' ? null : (
          <TextInput
            aria-label="Externally issued secret"
            type="password"
            value={providedSecret}
            placeholder="Optional external secret"
            onChange={(event) => setProvidedSecret(event.target.value)}
          />
        )}
        <Button
          size="small"
          leadingVisual={KeyIcon}
          disabled={providedSecret.length > 0 && providedSecret.length < 16}
          onClick={() => rotate.mutate(
            { authMode: rotateMode, ...(providedSecret.length === 0 ? {} : { secret: providedSecret }) },
            { onSuccess: (value) => { setProvidedSecret(''); onSecret(value); } },
          )}
        >Rotate / change auth</Button>
      </div>
      {rotateMode === 'none' || providedSecret.length > 0 ? null : (
        <p className="editor-hint">Leave the external secret empty to generate a new random one.</p>
      )}
      {editing ? (
        <WebhookEditor
          initial={initial}
          roles={roles}
          submitLabel="Save changes"
          busy={update.isPending}
          lockAuth
          onSubmit={(form) => update.mutate(form, { onSuccess: () => setEditing(false) })}
        />
      ) : null}
      <div className="webhook-batches">
        <strong>Recent batches</strong>
        {batches.isPending ? <p className="editor-hint">Reading batches…</p> : null}
        {(batches.data ?? []).slice(0, 5).map((batch) => (
          <div className="webhook-batch" key={batch.id}>
            <Chip tone={batchTone(batch.status)} text={batch.status} />
            <span>{batch.eventCount} events</span>
            <span>{new Date(batch.firstReceivedAt).toLocaleString()}</span>
            {batch.status === 'failed' ? <Button size="small" leadingVisual={SyncIcon} onClick={() => retry.mutate(batch.id)}>Retry</Button> : null}
            {batch.lastError === null ? null : <span className="webhook-error">{batch.lastError}</span>}
          </div>
        ))}
      </div>
    </article>
  );
}

function WebhookEditor({
  initial,
  roles,
  submitLabel,
  busy,
  lockAuth = false,
  onSubmit,
}: {
  readonly initial: WebhookForm;
  readonly roles: readonly string[];
  readonly submitLabel: string;
  readonly busy: boolean;
  readonly lockAuth?: boolean;
  readonly onSubmit: (form: WebhookForm) => void;
}): ReactNode {
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial.name, initial.targetRoleId, initial.enabled]);
  const validation = validateWebhookForm(form);
  const setNumber = (field: keyof WebhookForm, raw: string): void => setForm({ ...form, [field]: Number(raw) });
  return (
    <div className="webhook-editor">
      {validation.warning === null ? null : <Flash variant="warning">{validation.warning}</Flash>}
      <div className="webhook-form-grid">
        <FormControl><FormControl.Label>Name</FormControl.Label><TextInput block value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></FormControl>
        <FormControl><FormControl.Label>Agent</FormControl.Label><Select block value={form.targetRoleId} onChange={(event) => setForm({ ...form, targetRoleId: event.target.value })}>{roles.map((role) => <Select.Option key={role} value={role}>{role}</Select.Option>)}</Select></FormControl>
        <FormControl disabled={lockAuth}><FormControl.Label>Authentication</FormControl.Label><Select block disabled={lockAuth} value={form.authMode} onChange={(event) => setForm({ ...form, authMode: event.target.value as WebhookForm['authMode'] })}><Select.Option value="hmac-sha256">HMAC SHA-256</Select.Option><Select.Option value="bearer">Bearer token</Select.Option><Select.Option value="none">None</Select.Option></Select></FormControl>
        <FormControl><FormControl.Label>Debounce seconds</FormControl.Label><TextInput block type="number" value={form.debounceSeconds} onChange={(event) => setNumber('debounceSeconds', event.target.value)} /></FormControl>
        <FormControl><FormControl.Label>Requests per window</FormControl.Label><TextInput block type="number" value={form.rateLimitCount} onChange={(event) => setNumber('rateLimitCount', event.target.value)} /></FormControl>
        <FormControl><FormControl.Label>Rate window seconds</FormControl.Label><TextInput block type="number" value={form.rateLimitWindowSeconds} onChange={(event) => setNumber('rateLimitWindowSeconds', event.target.value)} /></FormControl>
        <FormControl><FormControl.Label>Maximum payload bytes</FormControl.Label><TextInput block type="number" value={form.maxPayloadBytes} onChange={(event) => setNumber('maxPayloadBytes', event.target.value)} /></FormControl>
        <FormControl><FormControl.Label>Payloads retained per batch</FormControl.Label><TextInput block type="number" value={form.maxBatchEvents} onChange={(event) => setNumber('maxBatchEvents', event.target.value)} /></FormControl>
        <FormControl><FormControl.Label>Signature header</FormControl.Label><TextInput block value={form.signatureHeader} onChange={(event) => setForm({ ...form, signatureHeader: event.target.value })} /></FormControl>
        <FormControl><FormControl.Label>Delivery ID header</FormControl.Label><TextInput block value={form.deliveryIdHeader} onChange={(event) => setForm({ ...form, deliveryIdHeader: event.target.value })} /></FormControl>
      </div>
      <FormControl><FormControl.Label>Wake-up message</FormControl.Label><Textarea block rows={4} value={form.messageTemplate} onChange={(event) => setForm({ ...form, messageTemplate: event.target.value })} /><FormControl.Caption>Webhook payloads are appended in a separate untrusted-data section.</FormControl.Caption></FormControl>
      <FormControl><Checkbox checked={form.enabled} onChange={(event) => setForm({ ...form, enabled: event.target.checked })} /><FormControl.Label>Enabled</FormControl.Label></FormControl>
      {validation.errors.length === 0 ? null : <Flash variant="danger">{validation.errors.join(' ')}</Flash>}
      <Button variant="primary" disabled={busy || validation.errors.length > 0} onClick={() => onSubmit(form)}>{busy ? 'Saving…' : submitLabel}</Button>
    </div>
  );
}
